import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { RELATION_MEMBER_OF, parseEntityRef } from '@backstage/catalog-model';
import { useEffect, useState } from 'react';

export type Team = { ref: string; title: string; path: string };

export function entityPath(ref: string): string {
  const { kind, namespace, name } = parseEntityRef(ref);
  return `/catalog/${namespace.toLowerCase()}/${kind.toLowerCase()}/${name}`;
}

// The signed-in user's groups, resolved to catalog Group entities. Shared by the
// sidebar's "Your Teams" item and the user footer, so both agree on membership.
export function useMyTeams(): { loading: boolean; teams: Team[] } {
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const [state, setState] = useState<{ loading: boolean; teams: Team[] }>({
    loading: true,
    teams: [],
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { userEntityRef } = await identityApi.getBackstageIdentity();
      const user = await catalogApi.getEntityByRef(userEntityRef);
      const groupRefs = (user?.relations ?? [])
        .filter(
          r =>
            r.type === RELATION_MEMBER_OF &&
            parseEntityRef(r.targetRef).kind.toLowerCase() === 'group',
        )
        .map(r => r.targetRef);

      if (groupRefs.length === 0) {
        if (!cancelled) setState({ loading: false, teams: [] });
        return;
      }

      const { items } = await catalogApi.getEntitiesByRefs({
        entityRefs: groupRefs,
      });
      const teams = groupRefs.map((ref, i) => {
        const entity = items[i];
        const profile = entity?.spec?.profile as
          | { displayName?: string }
          | undefined;
        const title =
          profile?.displayName ??
          entity?.metadata.title ??
          parseEntityRef(ref).name;
        return { ref, title, path: entityPath(ref) };
      });

      if (!cancelled) setState({ loading: false, teams });
    })().catch(() => {
      // A transient identity/catalog failure shouldn't leave every consumer
      // (the sidebar item, the footer, the Documentation landing page's
      // team-recommended lookup) stuck on `loading: true` forever — fail
      // open to "no teams" instead.
      if (!cancelled) setState({ loading: false, teams: [] });
    });
    return () => {
      cancelled = true;
    };
  }, [identityApi, catalogApi]);

  return state;
}
